// src/components/profile/DependentsSection.tsx
import React from 'react';
import { Plus, Users, Edit, X, RefreshCw } from 'lucide-react';
import { DependentRecord } from '../../pages/Profile'; // Adjust path

interface DependentCardProps {
    dependent: DependentRecord;
    onEdit: () => void;
    onDelete: () => void;
    getLabel: (group: string, value: string) => string;
}

const DependentCard: React.FC<DependentCardProps> = ({ dependent, onEdit, onDelete, getLabel }) => (
    <div className="bg-white rounded-xl p-2 sm:p-4 shadow-lg border border-gray-100 transition-all duration-300 hover:shadow-xl">
        <div className="flex justify-between items-start mb-2">
            <div>
                <h4 className="text-[12px] sm:text-xl font-semibold text-gray-900">{dependent.name}</h4>
                <p className="text-[11px] sm:text-sm text-gray-600">{getLabel('relation', dependent.relation)}</p>
            </div>
            <div className="flex space-x-1 sm:space-x-2">
                <button onClick={onEdit} className="p-1 sm:p-2 text-blue-600 hover:bg-blue-50 rounded-lg"><Edit className="w-4 h-4 sm:w-5 sm:h-5" /></button>
                <button onClick={onDelete} className="p-1 sm:p-2 text-red-600 hover:bg-red-50 rounded-lg"><X className="w-4 h-4 sm:w-5 sm:h-5" /></button>
            </div>
        </div>
        <div className="grid grid-cols-2 gap-2 sm:gap-4 text-[11px] sm:text-sm text-gray-600">
            <div className="space-y-1 sm:space-y-2">
                <p><span className="font-medium">Profile Type:</span> {getLabel('profileType', dependent.profileType)}</p>
                <p><span className="font-medium">Date of Birth:</span> {dependent.dob || 'N/A'}</p>
                <p><span className="font-medium">Gender:</span> {getLabel('gender', dependent.gender)}</p>
                <p><span className="font-medium">Marital Status:</span> {getLabel('maritalStatus', dependent.maritalStatus)}</p>
            </div>
            <div className="space-y-1">
                <p><span className="font-medium">Location:</span> {getLabel(`cities-${dependent.country}-${dependent.state}`, dependent.city)}, {getLabel(`states-${dependent.country}`, dependent.state)}, {getLabel('countries', dependent.country)}</p>
                <p><span className="font-medium">Currency:</span> {getLabel('currencies', dependent.currency)}</p>
                <p><span className="font-medium">Employment:</span> {getLabel('employmentStatus', dependent.employmentStatus)}</p>
                <p><span className="font-medium">Retirement Age:</span> {dependent.retirementAge ? `${dependent.retirementAge} Years` : 'N/A'}</p>
            </div>
        </div>
    </div>
);
    

interface DependentsSectionProps {
  dependents: DependentRecord[];
  apiLoading: boolean;
  onAdd: () => void;
  onEdit: (dependent: DependentRecord) => void;
  onDelete: (id: string) => void;
  getLabel: (group: string, value: string) => string;
}

const DependentsSection: React.FC<DependentsSectionProps> = ({ dependents, apiLoading, onAdd, onEdit, onDelete, getLabel }) => {
  return (
    <div className="bg-white rounded-2xl p-2 sm:p-8 shadow-lg border border-gray-100 h-full">
      <div className="flex items-center justify-between mb-2 sm:mb-4">
        <h3 className="text-[12px] sm:text-2xl font-semibold text-gray-900">Dependents</h3>
        <button onClick={onAdd} className="bg-blue-600 text-white py-2 sm:py-3 px-3 sm:px-4 rounded-lg hover:bg-blue-700 flex items-center">
          <Plus className="w-4 h-4 sm:w-6 sm:h-6 mr-1 sm:mr-2" />
          <span className="text-[11px] sm:text-base">Add Dependent</span>
        </button>
      </div>
      {apiLoading && (
        <div className="text-center py-6 sm:py-12">
          <RefreshCw className="w-6 h-6 sm:w-8 sm:h-8 animate-spin mx-auto text-blue-600" />
          <p className="mt-1 sm:mt-2 text-[11px] sm:text-base text-gray-600">Loading Dependents...</p>
        </div>
      )}
      {!apiLoading && dependents.length === 0 && (
        <div className="text-center py-6 sm:py-12">
          <Users className="w-8 h-8 sm:w-12 sm:h-12 mx-auto text-gray-400" />
          <p className="mt-2 text-gray-600">No dependents added yet.</p>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
        {dependents.map(dep => (
          <DependentCard
            key={dep.id}
            dependent={dep}
            getLabel={getLabel}
            onEdit={() => onEdit(dep)}
            onDelete={() => onDelete(dep.id)}
          />
        ))}
      </div>
    </div>
  );
};

export default DependentsSection;